import {
  IBrand,
  IItem,
  IStockOnHandLine,
  IStocktake,
  ISupplier,
  IUnit,
  SalesTax,
  StockOnHandLine,
  StockOnHandReport,
} from '$models'
import dayjs, { Dayjs } from 'dayjs'
import {
  GroupedPredictedOrderLines,
  IPredictedOrderLine,
  PredictedOrderRequestParams,
  PredictedOrderSlice,
} from '../slice'
import { SharedSlice } from 'src/components/slice'
import {
  posOrdersService,
  stockOnHandService,
  stockReceivedService,
} from '$services'
import { InvoiceDateProperty } from '$constants'
import { exceptionMessageHandler, roundUpToDecimalPlaces } from '$utils'
import { PurchaseOrderUiLine } from '../../purchase-orders/types'

/**
 * Combines the date part of one Dayjs object with the time part of another.
 *
 * @param date - A Dayjs object representing the desired date.
 * @param time - A Dayjs object representing the desired time.
 * @returns A new Dayjs object with the combined date and time.
 */
export function combineDateAndTime(date: Dayjs, time: Dayjs): Dayjs {
  return date
    .set('hour', time.hour())
    .set('minute', time.minute())
    .set('second', time.second())
    .set('millisecond', time.millisecond())
}

/**
 * Maps stock on hand lines to predicted order lines based on historical usage and predicted sales.
 *
 * @param totalPredictedSalesAmount - The expected total sales amount in the predicted period.
 * @param totalSalesAmount - The actual total sales amount in the reference period.
 * @param currentStockOnHandLines - Optional array of current stock on hand lines.
 * @param openingQuantityOnHandDict - Optional record mapping item IDs to their opening quantities.
 * @param closingQuantityOnHandDict - Optional record mapping item IDs to their closing quantities.
 * @param quantityReceivedDict - Optional record mapping item IDs to received quantities during the period.
 * @returns An array of grouped predicted order lines with usage estimates and suggested order quantities.
 */
export function transformToGroupedPredictedOrderLines(
  totalSalesAmount: number,
  totalBudgetSalesAmount: number,
  additionalBuffer: number,
  items: Record<string, IItem>,
  units: Record<string, IUnit>,
  currentStockOnHandLines?: IStockOnHandLine[],
  openingQuantityOnHandDict?: Record<string, number>,
  closingQuantityOnHandDict?: Record<string, number>,
  quantityReceivedDict?: Record<string, number>
): GroupedPredictedOrderLines[] {
  if (!currentStockOnHandLines) return []

  const filteredStockOnHandLines = currentStockOnHandLines.filter(
    (line) => !line.isItemDeleted
  )

  const predictedOrderLines = filteredStockOnHandLines.map(
    (line, idx): IPredictedOrderLine => {
      const itemId = line.itemId ?? ''
      const openingQuantityOnHand = openingQuantityOnHandDict?.[itemId] ?? 0
      const quantityReceived = quantityReceivedDict?.[itemId] ?? 0
      const closingQuantityOnHand = closingQuantityOnHandDict?.[itemId] ?? 0
      const quantityUsed =
        openingQuantityOnHand + quantityReceived - closingQuantityOnHand
      const totalPredictedSalesAmount = calculateBudgetSales(
        totalBudgetSalesAmount,
        additionalBuffer
      )
      const quantityOnHand = line.quantityOnHand ?? 0

      const predictedUsage = totalSalesAmount
        ? (quantityUsed / totalSalesAmount) * totalPredictedSalesAmount
        : 0

      const minimumStockOnHandQuantity =
        items[itemId]?.minimumStockOnHandQuantity
      const minimumStockOnHandUnitId = items[itemId]?.minimumStockOnHandUnitId
      const minimumStockOnHandUnitRatio =
        minimumStockOnHandUnitId && units[minimumStockOnHandUnitId].ratio
      const orderingUnitRatio = items[itemId]?.orderingUnitRatio

      const minimumStockOnHand = minimumStockOnHandUnitRatio
        ? convertCountingUnitToOrderingUnit(
            minimumStockOnHandQuantity,
            minimumStockOnHandUnitRatio,
            orderingUnitRatio
          )
        : 0

      const currentQuantityToOrder = calculateQuantityToOrderDetails(
        predictedUsage,
        quantityOnHand,
        minimumStockOnHand
      )

      return {
        lineId: window.crypto.randomUUID(),
        lineClass: () => '',
        stockItemName: line.itemName ?? 'Unknown Item',
        stockOnHandLine: {
          ...line,
          minimumStockOnHandQuantity: minimumStockOnHand,
          minimumStockOnHandUnitId: minimumStockOnHandUnitId,
        },
        quantityUsed: quantityUsed,
        isDeleted: false,
        predictedUsage: predictedUsage,
        // Preserve the initially calculated quantity to order.
        // Users can modify the value, but we still need to retain
        // and display the original calculated amount for reference.
        // initialQuantityToOrder: quantityToOrder,
        initialCurrentQuantityToOrder: currentQuantityToOrder,
        currentQuantityToOrder: currentQuantityToOrder,
        sortOrder: line.sortOrder ?? idx,
      }
    }
  )

  const groupedMap = new Map<string, GroupedPredictedOrderLines>()

  predictedOrderLines
    .sort((a, b) => a.sortOrder! - b.sortOrder!)
    .forEach((line, idx) => {
      const header = line.stockOnHandLine.header ?? 'Uncategorized'

      if (!groupedMap.has(header)) {
        groupedMap.set(header, {
          group: header,
          lines: [],
          sortOrder: idx,
        })
      }

      groupedMap.get(header)!.lines.push(line)
    })

  return Array.from(groupedMap.values()).sort((group) => group.sortOrder!)
}

/**
 * Converts a quantity from counting units to ordering units using given conversion ratios.
 *
 * @param quantity - The quantity in counting units to convert.
 * @param countingUnitRatio - The ratio representing how many base units are in one counting unit.
 * @param orderingUnitRatio - The ratio representing how many base units are in one ordering unit.
 * @returns The equivalent quantity in ordering units.
 */
export function convertCountingUnitToOrderingUnit(
  quantity?: number,
  countingUnitRatio?: number,
  orderingUnitRatio?: number
) {
  if (!quantity || !countingUnitRatio || !orderingUnitRatio) return 0

  return (quantity * countingUnitRatio) / orderingUnitRatio
}

/**
 * Calculate the budget sales using the additional buffer.
 *
 * @param budgetedSales - The total budget sales amount.
 * @param additionalBuffer - The additonal buffer.
 * @returns The total budget sales amount with the additonal buffer.
 */
export function calculateBudgetSales(
  budgetedSales?: number,
  additionalBuffer: number = 0
) {
  if (budgetedSales == null) return 0

  return budgetedSales + (budgetedSales * additionalBuffer) / 100
}

/**
 * Compares two PredictedOrderRequestParams objects and identifies which fields have changed.
 *
 * @param a - The first object (typically the "before" state)
 * @param b - The second object (typically the "after" state)
 * @returns An object containing only the fields that differ between a and b,
 *          with each changed field showing its 'from' and 'to' values
 */
export function getChangedFields(
  a: Partial<PredictedOrderRequestParams>,
  b: Partial<PredictedOrderRequestParams>
): Partial<
  Record<keyof PredictedOrderRequestParams, { from: unknown; to: unknown }>
> {
  const changes: Partial<
    Record<keyof PredictedOrderRequestParams, { from: unknown; to: unknown }>
  > = {}

  const keys: (keyof PredictedOrderRequestParams)[] = [
    'stocktakeTemplateId',
    'salesModelStartDate',
    'salesModelEndDate',
    'orderStockDateUntil',
  ]

  for (const key of keys) {
    const aVal = a[key]
    const bVal = b[key]

    const isDayjs = (val: unknown): val is Dayjs =>
      typeof val === 'object' && val !== null && 'isSame' in val

    const areEqual =
      isDayjs(aVal) && isDayjs(bVal) ? aVal.isSame(bVal) : aVal === bVal

    if (!areEqual) {
      changes[key] = { from: aVal, to: bVal }
    }
  }

  return changes
}

interface LoadReportsParams {
  get: () => PredictedOrderSlice & SharedSlice
  set: (
    partial:
      | (PredictedOrderSlice & SharedSlice)
      | Partial<PredictedOrderSlice & SharedSlice>
      | ((
          state: PredictedOrderSlice & SharedSlice
        ) =>
          | (PredictedOrderSlice & SharedSlice)
          | Partial<PredictedOrderSlice & SharedSlice>),
    replace?: boolean
  ) => void
  currentDateTime: dayjs.Dayjs
  stockOnHandOpeningDateTime: dayjs.Dayjs
  stockOnHandClosingDateTime: dayjs.Dayjs
  currentDateKey: string
  openingDateKey: string
  closingDateKey: string
  stocktakeTemplateId: string
  itemId?: string
}

/**
 * Loads key report data for a given stocktake template and date range.
 *
 * This helper function:
 * - Attempts to use cached stock on hand report responses if available.
 * - Catches and handles errors by resetting predicted order UI state and displaying a user-friendly message.
 *
 * @param get - Zustand-style state getter for accessing cached report responses.
 * @param set - Zustand-style state setter for resetting UI state on error.
 * @param currentDateTime - The datetime representing the "current" snapshot for stock on hand.
 * @param stockOnHandOpeningDateTime - Start of the reporting window (opening snapshot).
 * @param stockOnHandClosingDateTime - End of the reporting window (closing snapshot).
 * @param currentDateKey - Cache key for the current stock on hand report.
 * @param openingDateKey - Cache key for the opening stock on hand report.
 * @param closingDateKey - Cache key for the closing stock on hand report.
 * @param stocktakeTemplateId - The ID of the stocktake template to fetch reports for.
 * @param itemId - The ID of the item to fetch reports for.
 *
 * @returns A Promise resolving to an object containing the five reports:
 *          { currentStockOnHandReport, openingStockOnHandReport, closingStockOnHandReport, stockReceived, posOrders },
 *          or undefined if an error occurs.
 */
export const loadReportsHelper = async ({
  get,
  set,
  currentDateTime,
  stockOnHandOpeningDateTime,
  stockOnHandClosingDateTime,
  currentDateKey,
  openingDateKey,
  closingDateKey,
  stocktakeTemplateId,
  itemId,
}: LoadReportsParams) => {
  try {
    const stockOnHandReportResponsesDict =
      get().predictedOrderApiResponses?.stockOnHandReportApiResponses ?? {}

    const shouldRevalidate = get().predictedOrderApiResponses?.shouldRevalidate

    const cachedStockOnHandReportCurrent = shouldRevalidate
      ? undefined
      : stockOnHandReportResponsesDict[currentDateKey]?.[stocktakeTemplateId]
    const cachedStockOnHandReportOpening = shouldRevalidate
      ? undefined
      : stockOnHandReportResponsesDict[openingDateKey]?.[stocktakeTemplateId]
    const cachedStockOnHandReportClosing = shouldRevalidate
      ? undefined
      : stockOnHandReportResponsesDict[closingDateKey]?.[stocktakeTemplateId]

    const stockReceivedByDateRange =
      get().predictedOrderApiResponses?.stockReceivedApiResponses ?? {}

    const receivedItemsForPeriod =
      stockReceivedByDateRange[openingDateKey]?.[closingDateKey]

    const posOrdersByDateRange =
      get().predictedOrderApiResponses?.posOrdersApiResponses ?? {}

    const posOrdersForPeriod =
      posOrdersByDateRange[openingDateKey]?.[closingDateKey]

    const [
      currentStockOnHandReport,
      openingStockOnHandReport,
      closingStockOnHandReport,
      stockReceived,
      posOrders,
    ] = await Promise.all([
      // If no itemId is passed and we have cached data, return the cached data
      // Otherwise, make a fresh API call
      !itemId && cachedStockOnHandReportCurrent
        ? Promise.resolve(cachedStockOnHandReportCurrent)
        : stockOnHandService.getStockOnHand(
            stocktakeTemplateId,
            itemId,
            currentDateTime
          ),

      !itemId && cachedStockOnHandReportOpening
        ? Promise.resolve(cachedStockOnHandReportOpening)
        : stockOnHandService.getStockOnHand(
            stocktakeTemplateId,
            itemId,
            stockOnHandOpeningDateTime
          ),

      !itemId && cachedStockOnHandReportClosing
        ? Promise.resolve(cachedStockOnHandReportClosing)
        : stockOnHandService.getStockOnHand(
            stocktakeTemplateId,
            itemId,
            stockOnHandClosingDateTime
          ),

      receivedItemsForPeriod ??
        stockReceivedService.getStockReceivedItems(
          stockOnHandOpeningDateTime,
          stockOnHandClosingDateTime,
          InvoiceDateProperty.Received,
          undefined, //supplierId
          false, //includeAdjustingInvoices
          undefined, //itemId
          false //ifNoneGetLastReceived
        ),

      posOrdersForPeriod ??
        posOrdersService.getPosOrders(
          stockOnHandOpeningDateTime,
          stockOnHandClosingDateTime,
          undefined //interval
        ),
    ])

    const mergeLines = (
      cachedLines: StockOnHandLine[] = [],
      freshLines: StockOnHandLine[] = []
    ) => {
      const freshLinesByItemId = new Map(
        freshLines.map((line) => [line.itemId, line])
      )

      // Keep cached lines that don't have a fresh replacement, then add all fresh lines
      const mergedLines = cachedLines.filter(
        (line) => !freshLinesByItemId.has(line.itemId)
      )
      return [...mergedLines, ...freshLines]
    }

    return {
      currentStockOnHandReport: (itemId && cachedStockOnHandReportCurrent
        ? {
            ...cachedStockOnHandReportCurrent,
            ...currentStockOnHandReport,
            lines: mergeLines(
              cachedStockOnHandReportCurrent.lines,
              currentStockOnHandReport.lines
            ),
          }
        : currentStockOnHandReport) as StockOnHandReport,

      openingStockOnHandReport: (itemId && cachedStockOnHandReportOpening
        ? {
            ...cachedStockOnHandReportOpening,
            ...openingStockOnHandReport,
            lines: mergeLines(
              cachedStockOnHandReportOpening.lines,
              openingStockOnHandReport.lines
            ),
          }
        : openingStockOnHandReport) as StockOnHandReport,

      closingStockOnHandReport: (itemId && cachedStockOnHandReportClosing
        ? {
            ...cachedStockOnHandReportClosing,
            ...closingStockOnHandReport,
            lines: mergeLines(
              cachedStockOnHandReportClosing.lines,
              closingStockOnHandReport.lines
            ),
          }
        : closingStockOnHandReport) as StockOnHandReport,

      stockReceived,
      posOrders,
    }
  } catch (error) {
    set(() => ({
      predictedOrderUi: {
        groupedPredictedOrderLines: [],
        totalBudgetSalesAmount: undefined,
        totalSalesAmount: undefined,
        predictedOrderRequestParams: undefined,
      },
    }))
    exceptionMessageHandler(error, 'One or more report calls failed')
    return undefined
  }
}

export function calculateQuantityToOrderDetails(
  predictedUsage: number,
  quantityOnHand: number,
  minimumStockOnHand: number,
  enforceMinimumQuantityOnHand = true
): number {
  // Making sure we never pass a negative usage into further calculations
  const predictedUsageForOrdering = Math.max(predictedUsage, 0)

  const currentQuantityToOrder =
    (enforceMinimumQuantityOnHand ? minimumStockOnHand : 0) +
    predictedUsageForOrdering -
    quantityOnHand

  // If the number is negative the quantity to order should be 0
  const roundedCurrentQuantityToOrder = Math.max(
    roundUpToDecimalPlaces(currentQuantityToOrder, 0),
    0
  )

  return roundedCurrentQuantityToOrder
}

export function updateStockOnHandLineWithStocktake(
  singleStocktake: IStocktake,
  stockOnHandLine: IStockOnHandLine,
  orderingUnitRatio: number
): IStockOnHandLine | null {
  if (!singleStocktake?.lines) return null

  // The StocktakeLine objects and table store values differently from what's shown in the UI.
  // Instead of saving the displayed quantity, they store the value multiplied by the unit's ratio
  // effectively recording the count in base units rather than in the selected unit. (LOADED-5953)
  const countingUnitRatio = singleStocktake?.lines[1]?.unitRatio ?? 0
  const stocktakeQtyInCountingUnit =
    (singleStocktake?.lines[1]?.quantity ?? 0) / countingUnitRatio

  const stocktakeQtyInOrderingUnit = convertCountingUnitToOrderingUnit(
    stocktakeQtyInCountingUnit,
    countingUnitRatio,
    orderingUnitRatio
  )

  const receivedQuantity = stockOnHandLine?.receivedQuantity ?? 0

  const calculatedUsage = stockOnHandLine?.calculatedUsage ?? 0

  const quantityOnHand =
    stocktakeQtyInOrderingUnit + receivedQuantity - calculatedUsage

  // The current Stocktake schema isn't ideal, the first entry (lines[0]) is used as a header,
  // while the actual stocktake data starts at lines[1].
  // Fixing this properly will require a deeper DB refactor.
  // Since this function only processes a single stocktake, it's safe to reference lines[1] directly.
  const updatedStockOnHandLine: IStockOnHandLine = {
    ...stockOnHandLine,
    stocktakeQuantity: stocktakeQtyInCountingUnit,
    stocktakeUnitId: singleStocktake?.lines[1].unitId,
    stocktakeUnitName: singleStocktake?.lines[1].unitName,
    stocktakeUnitRatio: singleStocktake?.lines[1].unitRatio,
    stocktakeId: singleStocktake?.id,
    stocktakeTitle: singleStocktake?.title,
    stocktakeCompletedAt: singleStocktake?.completedAt,
    quantityOnHand: quantityOnHand,
  }

  return updatedStockOnHandLine
}

/**
 * Converts unit cost from last price unit to ordering unit
 * @param lastPriceCost The cost from the last price
 * @param lastPriceUnitRatio The unit ratio from the last price
 * @param orderingUnitRatio The ordering unit ratio for the item
 * @returns The converted unit cost in ordering units
 */
export const convertUnitCostToOrderingUnit = (
  lastPriceCost: number,
  lastPriceUnitRatio: number,
  orderingUnitRatio: number
): number => {
  if (!lastPriceUnitRatio || !orderingUnitRatio) return 0
  return (lastPriceCost * lastPriceUnitRatio) / orderingUnitRatio
}

/**
 * Transforms predicted order lines into purchase order UI lines for the review page
 * @param filteredPredictedOrderLines Grouped predicted order lines to transform
 * @param items Dictionary of all items
 * @param units Dictionary of all units
 * @param brands Dictionary of all brands
 * @param suppliers Dictionary of all suppliers
 * @param salesTax Sales tax rates indexed by sort order, used to derive line tax
 * @returns Array of PurchaseOrderUiLine for use in purchase order grid
 */
export const transformPredictedOrderLinesToPurchaseOrderLines = (
  filteredPredictedOrderLines: GroupedPredictedOrderLines[],
  items: Record<string, IItem>,
  units: Record<string, IUnit>,
  brands: Record<string, IBrand>,
  suppliers: Record<string, ISupplier>,
  salesTax: SalesTax[]
): PurchaseOrderUiLine[] => {
  return filteredPredictedOrderLines.flatMap((group) =>
    group.lines.flatMap((line) => {
      const itemId = line.stockOnHandLine.itemId
      const quantity = line.currentQuantityToOrder ?? 0

      // Skip deleted lines and lines with no quantity
      if (line.isDeleted || !itemId || quantity <= 0) {
        return []
      }

      const item = items[itemId]
      if (!item) {
        return []
      }

      const itemSupplierVariant = item.suppliers?.find(
        (s) => s.supplierId === item.defaultSupplierId
      )

      const supplierId =
        itemSupplierVariant?.supplierId ?? item.defaultSupplierId
      const supplierName = supplierId ? suppliers[supplierId]?.name : undefined
      const brandId = itemSupplierVariant
        ? itemSupplierVariant.brandId
        : item.defaultBrandId
      const brandName = brandId ? brands[brandId]?.name : undefined
      const unitId = itemSupplierVariant
        ? itemSupplierVariant.unitId
        : item.orderingUnitId
      const unitName = unitId ? units[unitId]?.name : undefined
      const unitRatio = unitId ? units[unitId]?.ratio : undefined

      const lastPriceCost = line.stockOnHandLine.lastPrice?.cost ?? 0
      const lastPriceUnitRatio = line.stockOnHandLine.lastPrice?.unitRatio ?? 0

      // Convert unit cost from last price unit to ordering unit using helper
      const convertedUnitCost = convertUnitCostToOrderingUnit(
        lastPriceCost,
        lastPriceUnitRatio,
        unitRatio ?? 1
      )

      const taxSortOrder = item?.globalSalesTaxSortOrder
      const taxRate =
        taxSortOrder != null ? salesTax?.[taxSortOrder]?.rate : undefined

      return [
        {
          lineId: line.lineId,
          __reorder__: item.name ?? '',
          stockItemId: itemId,
          itemId: itemId,
          itemName: item.name,
          itemCode: itemSupplierVariant?.stockCode,
          brandId: brandId,
          brandName: brandName,
          unitId: unitId,
          unitName: unitName,
          unitRatio: unitRatio,
          supplierId: supplierId,
          supplierName: supplierName,
          itemSupplierId: itemSupplierVariant?.id,
          quantityOrdered: quantity,
          unitCostOrdered: convertedUnitCost,
          taxPercent: taxRate,
          selectedSalesTax: taxSortOrder,
          customTaxRate: taxRate != null ? taxRate * 100 : 0,
          isEmpty: false,
          isEstimatedCostLoading: false,
        },
      ]
    })
  )
}
